import React, { useState, useRef } from 'react';
import { isPast, isToday, isTomorrow, format } from 'date-fns';
import { toast } from 'sonner';
import { formatDatePPP } from '../helpers/formatDateUTC';
import {
  ChevronDown,
  ChevronRight,
  Trash2,
  Calendar,
  Plus,
  MoreHorizontal,
  Check,
  Bot,
  Wrench,
  Play,
  Clock,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { useUpdateTodo, useDeleteTodo, useCreateStep, useCreateTodo } from '../helpers/useTodosApi';
import { proposeJudgement } from '../endpoints/todos/propose_judgement_POST.schema';
import { executeCandidate } from '../endpoints/todos/execute_candidate_POST.schema';
import { TodoItemWithSteps } from '../endpoints/todos/list_GET.schema';
import { Checkbox } from './Checkbox';
import { Badge } from './Badge';
import { Button } from './Button';
import { Input } from './Input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './Collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'; // Note: Using radix primitive directly if wrapper not available, or assume standard usage if available.
// Wait, available dependencies list @radix-ui/react-dropdown-menu but I don't have a local component wrapper in the provided code.
// I will implement a simple local dropdown or just use buttons for edit/delete to be safe and stick to provided components.
// Actually, I'll use a simple popover-like approach or just put the delete button in the expanded view.

import styles from './TodoItem.module.css';

interface TodoItemProps {
  todo: TodoItemWithSteps;
  onExecuteTask?: (taskId: string, taskTitle: string, taskDescription?: string) => void; // Callback to execute task with AI
  onExecuteSuggestion?: (suggestion: string) => void; // Execute suggestion via AI chat
}

export const TodoItem: React.FC<TodoItemProps> = ({ todo, onExecuteTask, onExecuteSuggestion }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newStepTitle, setNewStepTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState(todo.title);
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);
  const [newSuggestionText, setNewSuggestionText] = useState('');
  const [showNewInput, setShowNewInput] = useState(false);

  const { mutate: updateTodo } = useUpdateTodo();
  const { mutate: deleteTodo } = useDeleteTodo();
  const { mutate: createStep } = useCreateStep();
  const { mutate: createTodo } = useCreateTodo();

  // Parse suggestions from todo
  const suggestions: string[] = todo.suggestions ? JSON.parse(todo.suggestions) : [];
  const isProcessing = todo.generationStatus === 'processing';
  const isCompleted = todo.generationStatus === 'completed';

  // AI judgement prototype state
  const [proposals, setProposals] = useState<any[] | null>(null);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [executingCandidateId, setExecutingCandidateId] = useState<string | null>(null);

  const handleSuggestionToggle = (suggestion: string) => {
    setSelectedSuggestions((prev) =>
      prev.includes(suggestion)
        ? prev.filter((s) => s !== suggestion)
        : prev.length < 4
          ? [...prev, suggestion]
          : prev,
    );
  };

  const handleAddSelectedAsTodos = () => {
    const toAdd = [...selectedSuggestions];
    if (newSuggestionText.trim()) {
      toAdd.push(newSuggestionText.trim());
    }
    toAdd.forEach((suggestion) => {
      createTodo({ sessionId: todo.sessionId, title: suggestion });
    });
    setSelectedSuggestions([]);
    setNewSuggestionText('');
    setShowNewInput(false);
  };

  const completedSteps = todo.steps.filter((s) => s.isCompleted).length;
  const totalSteps = todo.steps.length;
  const progress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  const handleToggleComplete = () => {
    updateTodo({
      todoId: todo.id,
      isCompleted: !todo.isCompleted,
    });
  };

  const handleAddStep = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStepTitle.trim()) return;
    createStep({ todoId: todo.id, title: newStepTitle }, { onSuccess: () => setNewStepTitle('') });
  };

  const handleDeleteTodo = () => {
    if (confirm('Are you sure you want to delete this task?')) {
      deleteTodo({ todoId: todo.id });
    }
  };

  const handleTitleSave = () => {
    if (editTitleValue.trim() !== todo.title) {
      updateTodo({ todoId: todo.id, title: editTitleValue });
    }
    setIsEditingTitle(false);
  };

  const formatDateBadge = (date: Date) => {
    if (isPast(date) && !isToday(date) && !todo.isCompleted) {
      return (
        <Badge variant="destructive" className={styles.badge}>
          Overdue
        </Badge>
      );
    }
    if (isToday(date)) {
      return (
        <Badge variant="warning" className={styles.badge}>
          Today
        </Badge>
      );
    }
    if (isTomorrow(date)) {
      return (
        <Badge variant="secondary" className={styles.badge}>
          Tomorrow
        </Badge>
      );
    }
    return <span className={styles.dateText}>{format(date, 'MMM d')}</span>;
  };

  return (
    <div
      className={`${styles.container} ${todo.isCompleted ? styles.completed : ''} ${isProcessing ? styles.processing : ''}`}
    >
      <div className={styles.header}>
        <div className={styles.checkboxWrapper}>
          {isProcessing ? (
            <Loader2 size={18} className={styles.spinner} />
          ) : (
            <Checkbox
              checked={!!todo.isCompleted}
              onChange={handleToggleComplete}
              className={styles.mainCheckbox}
            />
          )}
        </div>

        <div className={styles.mainContent}>
          <div className={styles.titleRow} onClick={() => !isEditingTitle && setIsOpen(!isOpen)}>
            {isProcessing && (
              <Badge variant="secondary" className={styles.processingBadge}>
                <Loader2 size={12} className={styles.spinnerSmall} /> Processing...
              </Badge>
            )}
            {isEditingTitle ? (
              <Input
                value={editTitleValue}
                onChange={(e) => setEditTitleValue(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => e.key === 'Enter' && handleTitleSave()}
                autoFocus
                className={styles.titleInput}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span
                className={styles.title}
                onDoubleClick={() => {
                  setEditTitleValue(todo.title);
                  setIsEditingTitle(true);
                }}
              >
                {todo.title}
              </span>
            )}

            {todo.dueDate && (
              <div className={styles.metaBadge}>{formatDateBadge(new Date(todo.dueDate))}</div>
            )}
          </div>

          <div className={styles.metaRow} onClick={() => setIsOpen(!isOpen)}>
            {totalSteps > 0 && (
              <span className={styles.progressText}>
                <span
                  style={{
                    color:
                      progress === 100
                        ? '#22c55e'
                        : progress >= 75
                          ? '#f97316'
                          : progress >= 50
                            ? '#f59e0b'
                            : '#eab308',
                  }}
                >
                  {completedSteps}/{totalSteps}
                </span>{' '}
                steps
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{
                      width: `${progress}%`,
                      backgroundColor:
                        progress === 100
                          ? '#22c55e'
                          : progress >= 75
                            ? '#f97316'
                            : progress >= 50
                              ? '#f59e0b'
                              : '#eab308',
                    }}
                  />
                </div>
              </span>
            )}
            {/* Show processing indicator when AI is generating */}
            {isProcessing && (
              <div className={styles.processingBlock}>
                <span className={styles.progressText}>
                  <Loader2 size={12} className={styles.spinnerSmall} />
                  Generating...
                </span>
                {/* Show AI generation progress if available */}
                {todo.generationProgressPercent !== undefined &&
                todo.generationProgressPercent !== null ? (
                  <div className={styles.aiProgressRow}>
                    <div className={styles.aiProgressBar}>
                      <div
                        className={styles.aiProgressFill}
                        style={{ width: `${todo.generationProgressPercent}%` }}
                      />
                    </div>
                    <span className={styles.aiProgressLabel}>
                      {todo.generationProgressPercent}%
                    </span>
                  </div>
                ) : null}

                {todo.generationProgressMessage ? (
                  <div className={styles.aiProgressMessage}>{todo.generationProgressMessage}</div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon-sm"
          className={styles.expandBtn}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </Button>
      </div>

      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleContent className={styles.details}>
          {todo.description && <p className={styles.description}>{todo.description}</p>}

          {/* AI Judgement controls (prototype) */}
          <div className={styles.judgementControls}>
            <label className={styles.judgementToggle}>
              <input
                type="checkbox"
                checked={!!todo.ambiguous}
                onChange={(e) => updateTodo({ todoId: todo.id, ambiguous: e.target.checked })}
              />
              <span>Allow AI judgement</span>
            </label>

            <button
              className={`${styles.button} ${styles.secondary}`}
              onClick={async () => {
                try {
                  setLoadingProposals(true);
                  const res = await proposeJudgement({
                    sessionId: todo.sessionId,
                    todoId: todo.id,
                  });
                  setProposals(res.candidates || []);
                } catch (e: any) {
                  console.error('[TodoItem] propose error', e);
                  toast.error(e?.message || 'Failed to generate proposals');
                } finally {
                  setLoadingProposals(false);
                }
              }}
              disabled={loadingProposals}
            >
              {loadingProposals ? 'Generating...' : 'Generate Proposals'}
            </button>
          </div>

          {/* Proposals list */}
          {proposals && (
            <div className={styles.proposalsList}>
              <h4>Proposals</h4>
              {proposals.map((p) => (
                <div key={p.id} className={styles.proposalItem}>
                  <div className={styles.proposalMeta}>
                    <strong>{p.name}</strong>
                    <span className={styles.badge}>{p.risk.toUpperCase()}</span>
                    <span className={styles.score}>Score: {Math.round(p.score)}</span>
                    <span className={styles.eta}>{p.estMinutes}m</span>
                  </div>
                  <div className={styles.proposalDesc}>{p.description}</div>
                  <div className={styles.proposalActions}>
                    <button
                      className={styles.button}
                      onClick={async () => {
                        try {
                          setExecutingCandidateId(p.id);
                          await executeCandidate({
                            sessionId: todo.sessionId,
                            todoId: todo.id,
                            candidateId: p.id,
                          });
                          toast.success('Candidate execution started');
                          // Clear proposals after start
                          setProposals(null);
                        } catch (e: any) {
                          console.error('[TodoItem] executeCandidate error', e);
                          toast.error(e?.message || 'Failed to start execution');
                        } finally {
                          setExecutingCandidateId(null);
                        }
                      }}
                      disabled={!!executingCandidateId}
                    >
                      {executingCandidateId === p.id ? 'Starting...' : 'Execute'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className={styles.stepsList}>
            {todo.steps.map((step, idx) => (
              <div
                key={step.id}
                className={`${styles.stepItem} ${step.isCompleted ? styles.stepCompleted : ''}`}
              >
                <span className={styles.stepNumber}>{idx + 1}.</span>
                <span className={styles.stepTitle}>{step.title}</span>
                {step.isCompleted && <CheckCircle2 size={14} className={styles.stepDoneIcon} />}
              </div>
            ))}
          </div>

          <form onSubmit={handleAddStep} className={styles.addStepForm}>
            <Plus size={14} className={styles.addStepIcon} />
            <input
              id="todo-add-step"
              name="todo-add-step"
              type="text"
              placeholder="Add a step..."
              value={newStepTitle}
              onChange={(e) => setNewStepTitle(e.target.value)}
              className={styles.addStepInput}
            />
          </form>

          {/* AI Suggestions Section */}
          {isCompleted && suggestions.length > 0 && (
            <div className={styles.suggestionsSection}>
              <div className={styles.suggestionsHeader}>
                <img src="/suggestions.png" alt="Suggestions" className={styles.suggestionsIcon} />
                <span>AI Suggestions (select 1-4 to add as new todos)</span>
              </div>

              {/* Actionable suggestion cards */}
              <div className={styles.suggestionsList}>
                {suggestions.map((suggestion, idx) => (
                  <div key={idx} className={styles.suggestionCard}>
                    <div className={styles.suggestionContent}>{suggestion}</div>
                    <div className={styles.suggestionActions}>
                      {/* Execute only shown when onExecuteSuggestion provided */}
                      {onExecuteSuggestion && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onExecuteSuggestion(suggestion)}
                          className={styles.executeSuggestionBtn}
                        >
                          Execute
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Toggle as new todo selection for convenience
                          handleSuggestionToggle(suggestion);
                        }}
                      >
                        Select
                      </Button>
                    </div>
                  </div>
                ))}

                {/* New custom suggestion input */}
                <div className={styles.newSuggestionRow}>
                  {showNewInput ? (
                    <div className={styles.newSuggestionInput}>
                      <input
                        id="todo-custom-suggestion"
                        name="todo-custom-suggestion"
                        type="text"
                        placeholder="Enter custom suggestion..."
                        value={newSuggestionText}
                        onChange={(e) => setNewSuggestionText(e.target.value)}
                        className={styles.customInput}
                        autoFocus
                      />
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => {
                          setShowNewInput(false);
                          setNewSuggestionText('');
                        }}
                      >
                        <XIcon />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewInput(true)}
                      className={styles.addNewBtn}
                    >
                      <Plus size={14} /> New
                    </Button>
                  )}
                </div>
              </div>

              {(selectedSuggestions.length > 0 || newSuggestionText.trim()) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddSelectedAsTodos}
                  className={styles.addSuggestionsBtn}
                >
                  <Check size={14} /> Add{' '}
                  {selectedSuggestions.length + (newSuggestionText.trim() ? 1 : 0)} as Todo
                  {selectedSuggestions.length + (newSuggestionText.trim() ? 1 : 0) > 1 ? 's' : ''}
                </Button>
              )}
            </div>
          )}

          <div className={styles.footer}>
            <div className={styles.footerLeft}>
              {todo.dueDate && (
                <div className={styles.dueDateDisplay}>
                  <Calendar size={14} />
                  <span>Due {formatDatePPP(todo.dueDate)}</span>
                </div>
              )}

              {/* Tip card: show a small random tip when generation completed */}
              {isCompleted &&
                (() => {
                  const tips = [
                    'Tip: After code changes, ask the AI to generate unit tests for new components.',
                    'Tip: Run lint and format (pnpm lint / pnpm format) after bulk changes.',
                    'Tip: Consider adding a simple README for any new modules the AI added.',
                    'Tip: Ask AI to create or update integration tests for the new routes.',
                  ];
                  const idx = Math.abs(hashCode(todo.id)) % tips.length;
                  const tip = tips[idx];

                  return (
                    <div className={styles.tipCard}>
                      <div className={styles.tipText}>{tip}</div>
                      <div className={styles.tipActions}>
                        {onExecuteSuggestion && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              onExecuteSuggestion(`Implement the following suggestion: ${tip}`)
                            }
                          >
                            Ask AI
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => {}}>
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  );
                })()}
            </div>
            <div className={styles.footerRight}>
              {onExecuteTask && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={styles.executeBtn}
                  onClick={() => onExecuteTask(todo.id, todo.title, todo.description || undefined)}
                  title="Execute with AI code assistant"
                >
                  <Bot size={14} />
                  Execute
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className={styles.deleteBtn}
                onClick={handleDeleteTodo}
              >
                <Trash2 size={14} />
                Delete Task
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

// Simple deterministic hash for picking a tip per todo (stable across renders)
function hashCode(str: string) {
  let h = 2166136261 >>> 0; // FNV-1a 32-bit
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return h >>> 0;
}

// Simple X icon component for the delete button
const XIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
